import { createContext, useContext, type ReactNode } from 'react';

import { GasCurrencyUnitProvider } from './GasCurrencyUnitProvider';
import { GasDepositQuoteProvider } from './GasDepositQuoteProvider';
import type { GasDialogConfig } from './types';

import { useCustodiesListWithBalance } from '../../api/user/hooks/useCustodiesListWithBalance';
import { CustodySelectionProvider } from '../wallet-selector/WalletSelectedProvider';

const GasDialogConfigContext = createContext<GasDialogConfig | null>(null);

export function GasDialogConfigProvider({
  children,
  config,
}: {
  children: ReactNode;
  config: GasDialogConfig;
}) {
  const { isLoading: custodiesLoading, value: custodiesWithBalance } =
    useCustodiesListWithBalance({
      excludeVaults: false,
    });
  return (
    <GasDialogConfigContext.Provider value={config}>
      <CustodySelectionProvider
        custodiesWithBalance={custodiesWithBalance}
        isLoading={custodiesLoading}
        // JIT (pending execute / send / move) still needs a funding custody so
        // the Gas Account top-up card can quote and render. Send/move override
        // via useUpdateDefaultSelectedGasCustody when they have a from-wallet.
        shouldAutoSelectFirst
      >
        <GasDepositQuoteProvider topUpType={config.topUpType}>
          <GasCurrencyUnitProvider>{children}</GasCurrencyUnitProvider>
        </GasDepositQuoteProvider>
      </CustodySelectionProvider>
    </GasDialogConfigContext.Provider>
  );
}

export function useGasDialogConfig(): GasDialogConfig {
  const context = useContext(GasDialogConfigContext);
  if (!context) {
    throw new Error(
      'useGasDialogConfig must be used within a GasDialogConfigProvider'
    );
  }
  return context;
}
